import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { depthSectorsQueryOptions } from '@/features/depth/api/queries'
import { moversBrokersQueryOptions, moversTableQueryOptions } from '@/features/movers/api/queries'
import { MOVERS_VIEWS, moversQuerySchema, type MoversQuery } from '@/schemas/movers'
import type { TableQuery } from '@/schemas/table'
import type { RouterContext } from '@/routes/layouts/root'

/** The tabs: the five orderings of the movers table, then the Brokers feed. */
export const MOVERS_SCREEN_VIEWS = [...MOVERS_VIEWS, 'brokers'] as const

export type MoversScreenView = (typeof MOVERS_SCREEN_VIEWS)[number]

/**
 * `?view=` defaulted here rather than in MoversScreen.tsx because the loader
 * below parses the URL through it too, and the route's `validateSearch` and
 * the loader must not be able to disagree about which view a URL means.
 * `moversQuerySchema` is already the table hook's own shape; what this adds is
 * the landing view — a bare `/market/movers` means gainers — and the Brokers
 * tab, which is not one of the table's views.
 */
export const moversSearchSchema = moversQuerySchema.extend({
  view: z.enum(MOVERS_SCREEN_VIEWS).default('gainers'),
})

/**
 * The one parse both the loader and `MoversScreen` read `search` through.
 *
 * `Route.useSearch()` merges the URL's raw query string into the validated
 * object — an unrecognised param (`?utm_source=...`) rides along even though
 * `moversSearchSchema` never named it. `moversTableQueryOptions` keys on the
 * WHOLE query object, so that extra field would otherwise make the screen's
 * key differ, structurally, from the one this loader warmed for the entry
 * URL: a second request, and a visible pending-then-loaded flash on a link
 * nobody meant to change anything.
 */
export const toMoversQuery = (search: unknown) => moversSearchSchema.parse(search)

/**
 * The movers table's own query, or `null` on the Brokers tab, which reads another feed and has
 * no use for a view of the table.
 */
export const toTableQuery = (search: ReturnType<typeof toMoversQuery>): MoversQuery | null =>
  search.view === 'brokers' ? null : { ...search, view: search.view }

/**
 * The Brokers feed's query: the page and the sort, and nothing else of the URL. The sector and the
 * view are not part of what it fetches, and a key that carried them would be a second request.
 */
export const toBrokersQuery = (search: ReturnType<typeof toMoversQuery>): TableQuery => ({
  page: search.page,
  pageSize: search.pageSize,
  ...(search.sortBy === undefined ? {} : { sortBy: search.sortBy }),
  ...(search.sortDesc === undefined ? {} : { sortDesc: search.sortDesc }),
})

/**
 * The `/market/movers` loader.
 *
 * MAIN: the table for the ENTRY URL's query (view, sector, sort, page,
 * pageSize) — or the Brokers feed when that is the tab — awaited.
 *
 * SECONDARY, started and not awaited, and only on a real navigation
 * (`!preload`): the sector filter's own option list (`depthSectorsQueryOptions`,
 * the same factory `/market/depth`'s loader uses), for a table view only. Gated
 * on `preload` because the movers table is a dense link surface — every row
 * links to a stock — and a hover-intent preload must not also fire that request
 * as the cursor merely passes over the nav.
 *
 * `?view=`/`?sector=`/… are read from `location.search` through the route's
 * own schema, not through `loaderDeps`: a dep would make every tab, chip or
 * pager click a new route match — the full-page skeleton on each one. As it
 * is, a search-only change runs no loader at all; `MoversScreen` re-keys
 * `useMovers` for itself and dims the previous page while the new one loads
 * (`keepPreviousData`).
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree.
 */
export async function loadMovers({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = toMoversQuery(location.search)
  const table = toTableQuery(search)
  if (table === null) {
    await ensureRouteData(queryClient, moversBrokersQueryOptions(toBrokersQuery(search)))
    return
  }
  if (!preload) prefetchRouteData(queryClient, depthSectorsQueryOptions())
  await ensureRouteData(queryClient, moversTableQueryOptions(table))
}
